import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, ShoppingBag } from 'lucide-react';
import { Product } from '../../types';
import { useAuthStore } from '../../store/authStore';
import { useCartStore } from '../../store/cartStore';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import { useCurrencyStore } from '../../store/currencyStore';
interface ProductCardProps {
  product: Product;
  index?: number;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, index = 0 }) => {
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { isAuthenticated } = useAuthStore();
  const { addToCart } = useCartStore();
  const { format } = useCurrencyStore();
  const navigate = useNavigate();

  const primaryImage = product.images?.find((img) => img.is_primary)
    || product.images?.[0];
  const secondaryImage = product.images?.[1];

  const handleWishlist = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please login to save items');
      return;
    }
    try {
      if (isWishlisted) {
        await api.delete(`/api/users/wishlist/${product.id}`);
        setIsWishlisted(false);
        toast.success('Removed from wishlist');
      } else {
        await api.post(`/api/users/wishlist/${product.id}`);
        setIsWishlisted(true);
        toast.success('Added to wishlist');
      }
    } catch {
      toast.error('Failed to update wishlist');
    }
  };

  const handleQuickAdd = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      toast.error('Please login to add to bag');
      return;
    }

    // Made-to-measure pieces need their measurement form filled in first —
    // send the customer to the product page instead of a generic failure.
    if ((product as any).requires_measurements) {
      toast('This piece is made to measure — opening the product page so you can add your measurements.', { icon: 'ℹ️' });
      navigate(`/products/${product.slug}`);
      return;
    }

    if (!product.variants || product.variants.length === 0) {
      toast.error('Please select options on the product page');
      navigate(`/products/${product.slug}`);
      return;
    }

    const availableVariants = product.variants.filter((v) => v.stock_quantity > 0);
    if (availableVariants.length === 0) {
      toast.error('Out of stock');
      return;
    }

    // With more than one size/color option, silently guessing which one the
    // customer wants is worse than just asking — send them to the product
    // page to choose, rather than adding a variant they didn't pick.
    if (availableVariants.length > 1) {
      toast('Please choose your size and color on the product page', { icon: 'ℹ️' });
      navigate(`/products/${product.slug}`);
      return;
    }

    try {
      await addToCart(product.id, availableVariants[0].id, 1);
      toast.success('Added to bag');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to add to bag');
    }
  };

  const discount = product.compare_at_price
    ? Math.round(
        ((Number(product.compare_at_price) - Number(product.price)) /
          Number(product.compare_at_price)) *
          100
      )
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="product-card group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link to={`/products/${product.slug}`}>
        {/* Image Container */}
        <div className="relative overflow-hidden bg-light aspect-[3/4]">
          {/* Primary Image */}
          <motion.img
            src={primaryImage?.image_url || '/placeholder.jpg'}
            alt={product.name}
            className="w-full h-full object-cover absolute inset-0"
            animate={{ opacity: isHovered && secondaryImage ? 0 : 1 }}
            transition={{ duration: 0.4 }}
          />

          {/* Secondary Image on Hover */}
          {secondaryImage && (
            <motion.img
              src={secondaryImage.image_url}
              alt={product.name}
              className="w-full h-full object-cover absolute inset-0"
              animate={{ opacity: isHovered ? 1 : 0 }}
              transition={{ duration: 0.4 }}
            />
          )}

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col space-y-1">
            {product.is_featured && (
              <span className="bg-primary text-white text-xs px-2 py-1 tracking-widest uppercase">
                Featured
              </span>
            )}
            {discount && (
              <span className="bg-nude text-primary text-xs px-2 py-1 tracking-widest uppercase">
                -{discount}%
              </span>
            )}
          </div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.3 }}
            className="absolute bottom-0 left-0 right-0 p-3 flex space-x-2"
          >
            <button
              onClick={handleQuickAdd}
              className="flex-1 bg-white text-primary text-xs tracking-widest uppercase py-2 hover:bg-primary hover:text-white transition-colors flex items-center justify-center space-x-2"
            >
              <ShoppingBag size={12} />
              <span>Quick Add</span>
            </button>
          </motion.div>

          {/* Wishlist */}
          <button
            onClick={handleWishlist}
            className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-sm hover:bg-light transition-colors"
          >
            <Heart
              size={14}
              className={isWishlisted ? 'fill-primary text-primary' : 'text-primary'}
            />
          </button>
        </div>

        {/* Product Info */}
        <div className="pt-3 space-y-1">
          {product.category_name && (
            <p className="text-xs text-nude tracking-widest uppercase">
              {product.category_name}
            </p>
          )}
          <h3 className="font-serif text-base text-primary group-hover:text-secondary transition-colors">
            {product.name}
          </h3>
         <div className="flex items-center space-x-2">
  <span className="text-sm text-primary">
    {format(Number(product.price))}
  </span>
  {product.compare_at_price && (
    <span className="text-xs text-nude line-through">
      {format(Number(product.compare_at_price))}
    </span>
  )}
</div>

          {/* Color Swatches */}
          {product.variants && product.variants.length > 0 && (
            <div className="flex space-x-1 pt-1">
              {Array.from(
                new Map(
                  product.variants
                    .filter((v) => v.color_hex)
                    .map((v) => [v.color_hex, v])
                ).values()
              )
                .slice(0, 5)
                .map((variant) => (
                <div
                  key={variant.id}
                  className="w-3 h-3 rounded-full border border-light"
                  style={{ backgroundColor: variant.color_hex || '#ccc' }}
                  title={variant.color || ''}
                />
              ))}
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;